/** Minimal candle shape needed for entry time snapping. */
export type CandleTimePoint = { time: number };

/**
 * Return candle open that contains target time: latest candle time <= target.
 * Returns null when target is before the first candle (no containing bar).
 */
export function snapToContainingCandleTime(
  candles: CandleTimePoint[],
  target: number | null
): number | null {
  if (target == null || !candles.length) return target;
  if (target < candles[0].time) return null;
  let lo = 0;
  let hi = candles.length - 1;
  let ans = candles[0].time;
  while (lo <= hi) {
    const mid = Math.floor((lo + hi) / 2);
    const t = candles[mid].time;
    if (t <= target) {
      ans = t;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return ans;
}

/**
 * Plot entry on the candle series. Entries before the first bar (weekend / session gap)
 * that still overlap the series are parked on the first candle without OHLC clamping,
 * so true entry price stays visible.
 */
export function resolveEntryPlotOnCandles(
  candles: CandleTimePoint[],
  rawEntry: number | null,
  rawExit: number | null | undefined
): { time: number; beforeSeries: boolean } | null {
  if (rawEntry == null || !candles.length) return null;
  const first = candles[0].time;
  if (rawEntry < first) {
    const overlapsSeries = rawExit == null || rawExit >= first;
    if (!overlapsSeries) return null;
    return { time: first, beforeSeries: true };
  }
  const snapped = snapToContainingCandleTime(candles, rawEntry);
  if (snapped == null) return null;
  return { time: snapped, beforeSeries: false };
}
